import { NavLink, Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  generalIcons,
  preStakingUrl,
  vanarExplorerUrl,
} from "../../utils/constants";
import MenuButton from "./MenuButton";
import { useCycle } from "framer-motion";
import WalletInfo from "./WalletInfo";
import LoginButton from "./LoginButton";
import LogoutButton from "./LogoutButton";
import { useRootContext } from "../../context/app-context";
import { useWalletConnect } from "../../context/wallet-connection";
import { useStakingContext } from "../../context/staking-context";
import useVanryData from "../../hooks/useVanryData";

const navigationLinks = [
  { name: "My Account", url: "/my-account", target: "_self", id: "myAccount" },
  { name: "Calculator", url: "/calculator", target: "_self", id: "calculator" },
  { name: "FAQs", url: "/faqs", target: "_self", id: "faqs" },
  {
    name: "Guide",
    url: "https://docs.vanarchain.com/nodes-and-validators/staking",
    target: "_blank",
    id: "guide",
    icon: true,
  },
  {
    name: "Vanar Explorer",
    url: vanarExplorerUrl,
    target: "_blank",
    id: "vanarExplorer",
    icon: true,
  },
  {
    name: "Support",
    url: "https://t.me/Van_AI_bot",
    target: "_blank",
    id: "support",
    icon: true,
  },
  {
    name: "Claim Prestake Rewards",
    url: preStakingUrl,
    target: "_blank",
    id: "claimPrestakeRewards",
    icon: true,
  },
];

export default function Header() {
  const [scroll, setScroll] = useState(false);
  const { isSignWallet } = useRootContext();
  const [isOpen, toggleDropdown] = useCycle(false, true);
  const { address, connector, handleDisconnectWallet, isAuthenticated } =
    useWalletConnect();
  const { availableBalance, IsFetchingWalletInfo } = useStakingContext();
  const { vanryPrice } = useVanryData({setIsDataFetched: ()=>{}});

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    window.addEventListener("scroll", () => {
      setScroll(window.scrollY > 50);
    });
  }, []);

  const bodyElement = document.body;
  isOpen
    ? bodyElement.classList.add("overflow-hidden")
    : bodyElement.classList.remove("overflow-hidden");

  return (
    <>
      <nav
        className={`fixed top-0 w-full z-50 bg-vampireBlack/70 border-b-[1.5px] border-b-white/[.04] backdrop-blur-[15px] duration-300 ease-linear ${
          scroll ? "py-3 lg:py-[15px]" : "py-4 lg:py-[17.61px]"
        }`}
      >
        <div className="navbar-nav max-w-container flex flex-wrap items-center justify-between mx-auto px-3 lg:px-[20px] xl:px-[32px]">
          <div className="relative flex items-center gap-5 laptop:gap-5 xl:gap-10">
            <Link
              to="/"
              className="relative flex items-center w-[164px] lg:w-[180px] xl:w-[255px] space-x-3 rtl:space-x-reverse z-20"
              onClick={() => {
                scrollToTop();
              }}
            >
              <img
                src={`${process.env.REACT_APP_S3_ASSETS_URL}vanar-staking.svg`}
                alt="logo"
              />
            </Link>

            <ul className="hidden laptop:flex items-center justify-center gap-5 laptop:gap-[14px] xl:gap-6 self-end">
              {(isAuthenticated
                ? navigationLinks
                : navigationLinks?.filter((e) => e.name !== "My Account")
              ).map((navLink) => (
                <li
                  key={navLink.name}
                  className="nav-link text-base lg:text-xsm xxl:text-base font-medium flex items-center justify-center capitalize"
                >
                  <NavLink
                    id={navLink.id}
                    to={navLink.url}
                    target={navLink.target}
                    className={({ isActive }) =>
                      `hover:text-green flex items-center justify-center gap-2 duration-300 text-white`
                    }
                  >
                    {navLink.name}
                    {navLink.icon && (
                      <span className="w-3 h-3 laptop:w-4 laptop:h-4 flex items-center justify-center">
                        {generalIcons.externalLink}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative flex items-center gap-3 lg:gap-5 z-20">
            <div className="hidden md:flex flex-row space-x-2 lg:space-x-3 xl:space-x-4 space-y-0">
              {(!isSignWallet || (!isAuthenticated && isSignWallet)) && (
                <LoginButton />
              )}
              {isSignWallet && isAuthenticated && (
                <>
                  <WalletInfo
                    availableBalance={availableBalance}
                    coinUsdValue={vanryPrice}
                    IsFetchingWalletInfo={IsFetchingWalletInfo}
                  />
                  <LogoutButton
                    walletAddress={address}
                    connectedWallet={connector?.id}
                    disconnectWallet={handleDisconnectWallet}
                  />
                </>
              )}
            </div>

            <MenuButton onClick={toggleDropdown} isOpen={isOpen} />
          </div>
        </div>
      </nav>

      <div
        className={`fixed top-0 w-screen h-[100dvh] bg-black z-40 duration-300 ${
          isOpen ? "left-0" : "-left-full"
        }`}
      >
        <div className="relative flex flex-col justify-between gap-10 mb-4 p-4 pb-8 pt-[90px] h-full bg-black overflow-x-auto">
          <ul className="flex flex-col gap-6 md:gap-8 z-10 overflow-x-auto">
            {(isAuthenticated
              ? navigationLinks
              : navigationLinks?.filter((e) => e.name !== "My Account")
            ).map((navLink) => (
              <li
                key={navLink.name}
                className="nav-link text-sm md:text-base font-medium flex items-center capitalize"
                onClick={toggleDropdown}
              >
                <NavLink
                  to={navLink.url}
                  target={navLink.target}
                  className={({ isActive }) =>
                    `hover:text-green flex items-center justify-center gap-2 duration-300 w-full text-white`
                  }
                >
                  {navLink.name}
                  {navLink.icon && (
                    <span className="w-3 h-3 laptop:w-4 laptop:h-4 flex items-center justify-center">
                      {generalIcons.externalLink}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="w-full flex flex-col gap-5">
            {!isSignWallet && <LoginButton className="w-full *:w-full" />}
            {isSignWallet && (
              <>
                <WalletInfo
                  availableBalance={availableBalance}
                  coinUsdValue={vanryPrice}
                />
                <LogoutButton
                  className="w-full"
                  walletAddress={address}
                  connectedWallet={connector?.id}
                  disconnectWallet={handleDisconnectWallet}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
